import {
  Modal,
  Row,
  Col,
  Badge,
  Nav,
  Button,
} from "react-bootstrap";
import { useState } from "react";
import {
  FaUser,
  FaIdCard,
  FaCertificate,
  FaPhone,
  FaFileAlt,
  FaTruck,
  FaStar,
  FaEnvelope,
  FaCog,
  FaRoad,
} from "react-icons/fa";

import { FaPlus } from "react-icons/fa";
import { useAuth } from "../../hooks/useAuth";
import CachedIcon from "mdi-react/CachedIcon";
import DocumentUploadModal from "./DocumentUploadModal";
import { fetchActionsUtil } from "../../utils/helpers";
import TripDetailModal from "./TripDetailModal";
import ModalLoader from "../utils/ModalLoader";
import TableComponent from "../TableComponent";
import DocumentsTab from "./DocumentsTab";

function DriverDetailsModal({ showModal, setShowModal, driver, refetch }) {
  const { backendUrl } = useAuth();
  const [activeTab, setActiveTab] = useState("personal");
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [tripDetails, setTripDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  if (!driver) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };


  const getStatusBadge = (status) => {
    const statusMap = {
      active: { variant: "success", text: "Active" },
      inactive: { variant: "secondary", text: "Inactive" },
      suspended: { variant: "warning", text: "Suspended" },
      terminated: { variant: "danger", text: "Terminated" },
    };
    const statusInfo = statusMap[status] || {
      variant: "secondary",
      text: status,
    };
    return <Badge bg={statusInfo.variant}>{statusInfo.text}</Badge>;
  };

  const getTripStatusBadge = (status) => {
    const statusMap = {
      completed: { variant: "success", text: "Completed" },
      in_progress: { variant: "primary", text: "In Progress" },
      planned: { variant: "info", text: "Planned" },
      cancelled: { variant: "danger", text: "Cancelled" },
      delayed: { variant: "warning", text: "Delayed" },
    };
    const statusInfo = statusMap[status] || {
      variant: "secondary",
      text: status,
    };
    return (
      <Badge bg={statusInfo.variant} className="text-uppercase">
        {statusInfo.text}
      </Badge>
    );
  };

  const isExpiringSoon = (dateString) => {
    if (!dateString) return false;
    const expiryDate = new Date(dateString);
    const today = new Date();
    const daysUntilExpiry = Math.ceil(
      (expiryDate - today) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
  };

  const isExpired = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  // Get initials for avatar
  const getInitials = (employeeId) => {
    if (!employeeId) return "D";
    return employeeId.substring(0, 1).toUpperCase();
  };

  const openTripDetails = async (id) => {
    try {
      setIsLoading(true);
      const { data } = await fetchActionsUtil(
        `${backendUrl}/api/fleet/trips?id=${id}`,
        "GET"
      );
      console.log("tripDetails: ", data.trips);
      setTripDetails(data.trips[0]);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = () => {
    setShowAddDocumentModal(true);
  };

  const tripTableHead = () => (
    <thead>
      <tr>
        <th>Trip Number</th>
        <th>Purpose</th>
        <th>Start Date</th>
        <th>Distance</th>
        <th>Status</th>
      </tr>
    </thead>
  );

  const tripTableData = (trip) => (
    <>
      <td className="fw-medium">{trip.tripNumber}</td>
      <td>{trip.purpose}</td>
      <td>{formatDate(trip.plannedStartTime)}</td>
      <td>{trip.actualDistance || trip.plannedDistance || "N/A"} miles</td>
      <td>{getTripStatusBadge(trip.status)}</td>
    </>
  );

  return (
    <>
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        className="driver-profile-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <h3>
              {driver.Title} {driver.Name}
              <Button variant="">
                <CachedIcon
                  onClick={() => refetch()}
                  className="text-primary"
                />{" "}
              </Button>
            </h3>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {/* Header Section */}
          <div className="profile-header">
            <div className="profile-header-content">
              <div className="profile-avatar-section">
                {driver.picturePath ? (
                  <img
                    src={`${backendUrl}/static${driver.picturePath}`}
                    alt={driver.employee_id}
                    className="profile-avatar"
                  />
                ) : (
                  <div className="profile-avatar-placeholder">
                    <span>
                      {getInitials(driver.Name || driver.employee_id)}
                    </span>
                  </div>
                )}
              </div>

              <div className="profile-info">
                <div className="profile-name-section">
                  <h3 className="profile-name">
                    {driver.Title} {driver.Name}
                  </h3>
                  {/* <div className="profile-meta">
                  <FaEnvelope className="me-2" />
                  <span>Employee ID: {driver.Staff_ID}</span>

                  <div className="status-badge-wrapper">
                    {getStatusBadge(driver.status)}
                  </div>
                </div> */}
                </div>

                <div className="profile-stats">
                  <div className="stat-item">
                    <FaIdCard className="stat-icon" />
                    <div className="stat-content">
                      <span className="stat-label">License</span>
                      <span className="stat-value">
                        Class {driver.licenseClass}
                      </span>
                    </div>
                  </div>

                  <div className="stat-item">
                    <FaPhone className="stat-icon" />
                    <div className="stat-content">
                      <span className="stat-label">Phone</span>
                      <span className="stat-value">
                        {driver.emergencyContactPhone || "N/A"}
                      </span>
                    </div>
                  </div>

                  <div className="stat-item">
                    <FaRoad className="stat-icon" />
                    <div className="stat-content">
                      <span className="stat-label">Completed Trips</span>
                      <span className="stat-value">
                        {driver.trips.length || "N/A"}
                      </span>
                    </div>
                  </div>

                  <div className="reference-card">
                    <FaUser className="reference-icon" />
                    <div className="reference-content">
                      <span className="reference-label">Safety Score</span>
                      <div className="safety-score">
                        <FaStar className="text-warning me-1" />
                        <span className="score-value">
                          {driver.safetyScore || 0}
                        </span>
                        <span className="score-max"> / 100</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* <div className="profile-actions">
              <div className="profile-status-card">
                <div className="membership-type">
                  <span className="membership-label">Status</span>
                </div>
              </div>
            </div> */}
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="profile-nav">
            <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab}>
              <Nav.Item>
                <Nav.Link eventKey="personal">
                  <FaUser className="me-2" />
                  Personal Details
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="license">
                  <FaIdCard className="me-2" />
                  License Information
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="trips">
                  <FaTruck className="me-2" />
                  Trip History
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="documents">
                  <FaFileAlt className="me-2" />
                  Documents
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="settings">
                  <FaCog className="me-2" />
                  Account Settings
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </div>

          {/* Tab Content */}
          <div className="profile-content">
            {activeTab === "personal" && (
              <div className="tab-content-wrapper">
                <Row>
                  <Col md={6}>
                    <div className="info-card">
                      <h6 className="info-card-title">
                        <FaUser className="me-2" />
                        Basic Information
                      </h6>
                      <div className="info-grid">
                        <div className="info-item">
                          <span className="info-label">Staff ID</span>
                          <span className="info-value">{driver.Staff_ID}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Driver ID</span>
                          <span className="info-value">{driver.driver_id}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Status</span>
                          <span className="info-value">
                            {getStatusBadge(driver.status)}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Safety Score</span>
                          <span className="info-value">
                            <FaStar className="text-warning me-1" />
                            {driver.safetyScore || 0} / 100
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Department</span>
                          <span className="info-value">
                            {driver.Department}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Branch</span>
                          <span className="info-value">{driver.Branch}</span>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <Col>
                      <div className="info-card">
                        <h6 className="info-card-title">
                          <FaPhone className="me-2" />
                          Emergency Contact
                        </h6>
                        <div className="info-grid">
                          <div className="info-item">
                            <span className="info-label">Contact Name</span>
                            <span className="info-value">
                              {driver.emergencyContactName || "N/A"}
                            </span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Phone Number</span>
                            <span className="info-value">
                              {driver.emergencyContactPhone || "N/A"}
                            </span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Relationship</span>
                            <span className="info-value">
                              {driver.emergencyContactRelation || "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Col>

                    <Col>
                      <div className="info-card">
                        <h6 className="info-card-title">
                          <FaEnvelope className="me-2" />
                          Contact & Account Info
                        </h6>
                        <div className="info-grid">
                          <div className="info-item">
                            <span className="info-label">Email</span>
                            <span className="info-value">{driver.email}</span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Phone</span>
                            <span className="info-value">
                              {driver.contactPhone}
                            </span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">User Type</span>
                            <span className="info-value">
                              {driver.userType}
                            </span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Job Grade</span>
                            <span className="info-value">
                              {driver.jobGrade}
                            </span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Access Level</span>
                            <span className="info-value">
                              {driver.AccessLavel}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Col>

                  {driver.notes && (
                    <Col md={12}>
                      <div className="info-card">
                        <h6 className="info-card-title">
                          <FaFileAlt className="me-2" />
                          Additional Notes
                        </h6>
                        <p className="notes-text">{driver.notes}</p>
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            )}
            {activeTab === "license" && (
              <div className="tab-content-wrapper">
                <Row>
                  <Col md={6}>
                    <div className="info-card">
                      <h6 className="info-card-title">
                        <FaIdCard className="me-2" />
                        Driver's License
                      </h6>
                      <div className="info-grid">
                        <div className="info-item">
                          <span className="info-label">License Number</span>
                          <span className="info-value">
                            {driver.licenseNumber}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">License Class</span>
                          <span className="info-value">
                            Class {driver.licenseClass}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Expiry Date</span>
                          <span className="info-value">
                            <span
                              className={
                                isExpired(driver.licenseExpiry)
                                  ? "text-danger fw-bold"
                                  : isExpiringSoon(driver.licenseExpiry)
                                  ? "text-warning fw-bold"
                                  : ""
                              }
                            >
                              {formatDate(driver.licenseExpiry)}
                            </span>
                            {isExpired(driver.licenseExpiry) && (
                              <Badge bg="danger" className="ms-2">
                                Expired
                              </Badge>
                            )}
                            {!isExpired(driver.licenseExpiry) &&
                              isExpiringSoon(driver.licenseExpiry) && (
                                <Badge bg="warning" className="ms-2">
                                  Expiring Soon
                                </Badge>
                              )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="info-card">
                      <h6 className="info-card-title">
                        <FaCertificate className="me-2" />
                        Medical Certificate
                      </h6>
                      <div className="info-grid">
                        <div className="info-item">
                          <span className="info-label">Expiry Date</span>
                          <span className="info-value">
                            <span
                              className={
                                isExpired(driver.medicalCertExpiry)
                                  ? "text-danger fw-bold"
                                  : isExpiringSoon(driver.medicalCertExpiry)
                                  ? "text-warning fw-bold"
                                  : ""
                              }
                            >
                              {formatDate(driver.medicalCertExpiry)}
                            </span>
                            {isExpired(driver.medicalCertExpiry) && (
                              <Badge bg="danger" className="ms-2">
                                Expired
                              </Badge>
                            )}
                            {!isExpired(driver.medicalCertExpiry) &&
                              isExpiringSoon(driver.medicalCertExpiry) && (
                                <Badge bg="warning" className="ms-2">
                                  Expiring Soon
                                </Badge>
                              )}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Status</span>
                          <span className="info-value">
                            {isExpired(driver.medicalCertExpiry) ? (
                              <Badge bg="danger">Expired</Badge>
                            ) : (
                              <Badge bg="success">Valid</Badge>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            )}
            {activeTab === "trips" && (
              <div className="tab-content-wrapper">
                {driver.trips && driver.trips.length > 0 ? (
                  <div className="table-card">
                    <div className="table-card-header">
                      <h6>
                        <FaTruck className="me-2" />
                        Trip History ({driver.trips.length})
                      </h6>
                    </div>
                    <div className="table-responsive">
                      <TableComponent
                        striped
                        responsive
                        borderless
                        hover
                        tableRowClick={(el, trip) =>
                          openTripDetails(trip.trip_id)
                        }
                        // className="modern-table"
                        tableHeadsFunction={tripTableHead}
                        tableDataRowFunction={tripTableData}
                        className="product-table text-nowrap"
                        mainDataArray={driver.trips}
                      />
                    </div>
                  </div>
                ) : (
                  // </div>
                  <div className="empty-state">
                    <FaTruck size={48} className="empty-icon" />
                    <p>No trip history available</p>
                  </div>
                )}
              </div>
            )}
            {activeTab === "documents" && (
              <DocumentsTab documents={driver.documents} />
            )}
            {activeTab === "settings" && (
              <div className="tab-content-wrapper">
                <Row>
                  <Col md={6}>
                    <div className="info-card">
                      <h6 className="info-card-title">Account Status</h6>
                      <div className="info-grid">
                        <div className="info-item">
                          <span className="info-label">Status</span>
                          <span className="info-value">
                            {getStatusBadge(driver.status)}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Email Verification</span>
                          <span className="info-value">
                            <Badge
                              bg={
                                driver.isEmailVerified ? "success" : "warning"
                              }
                            >
                              {driver.isEmailVerified
                                ? "Verified"
                                : "Unverified"}
                            </Badge>
                          </span>
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="info-card">
                      <h6 className="info-card-title">Registration Date</h6>
                      <div className="info-grid">
                        <div className="info-item">
                          <span className="info-label">Created</span>
                          <span className="info-value">
                            {formatDate(driver.createdAt)}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Last Updated</span>
                          <span className="info-value">
                            {formatDate(driver.updatedAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer className="profile-footer">
          <Button
            variant="outline-secondary"
            onClick={() => setShowModal(false)}
          >
            Close
          </Button>
          {activeTab === "documents" && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleOpenModal}
              className="add-doc-btn"
            >
              <FaPlus className="me-1" /> Add Document
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      <DocumentUploadModal
        entity={"Driver"}
        entity_id={driver.driver_id}
        showModal={showAddDocumentModal}
        setShowModal={setShowAddDocumentModal}
        onDocumentAdded={refetch}
      />
      <TripDetailModal
        backendUrl={backendUrl}
        onHide={() => setTripDetails(null)}
        show={!!tripDetails}
        trip={tripDetails}
      />

      <ModalLoader show={isLoading} title={"Fetching trip details"} />
    </>
  );
}

export default DriverDetailsModal;
